/**
 * ProgressTimeline Component
 *
 * Displays chronological list of progress entries.
 * Story 6-5
 */

import { PopProgressEntry } from '../../lib/popApi';

interface ProgressTimelineProps {
  entries: PopProgressEntry[];
  canDelete: boolean;
  onDelete: (entryId: number) => void;
}

export function ProgressTimeline({ entries, canDelete, onDelete }: ProgressTimelineProps) {
  if (entries.length === 0) {
    return (
      <div className="text-center py-8">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <p className="mt-2 text-sm text-gray-500">Nog geen voortgang geregistreerd</p>
      </div>
    );
  }

  return (
    <div data-testid="progress-timeline" className="flow-root">
      <ul className="-mb-8">
        {entries.map((entry, idx) => (
          <li key={entry.id} data-testid="progress-note">
            <div className="relative pb-8">
              {/* Timeline line */}
              {idx !== entries.length - 1 && (
                <span
                  className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
                  aria-hidden="true"
                />
              )}

              <div className="relative flex items-start space-x-3">
                {/* Icon */}
                <div className="relative">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center ring-8 ring-white">
                    <svg
                      className="h-5 w-5 text-blue-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1">
                  <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-medium text-gray-900">
                            {entry.authorName}
                          </span>
                          <span className="text-sm text-gray-500">•</span>
                          <time className="text-sm text-gray-500">
                            {new Date(entry.progressDate).toLocaleDateString('nl-NL', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </time>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {entry.content}
                        </p>
                      </div>

                      {canDelete && (
                        <button
                          onClick={() => {
                            if (confirm('Weet je zeker dat je deze voortgang wilt verwijderen?')) {
                              onDelete(entry.id);
                            }
                          }}
                          className="ml-4 p-1 text-gray-400 hover:text-red-600"
                          title="Verwijderen"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
